// ===== 一覧 =====

import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import { WorkSelectField } from "../../../components/input/WorkSelectField";
import { useNavigate } from "react-router-dom";
import { PageContext } from "../PageContext";
import Time from "../../../util/Time";
import { DataLink } from "../../../components/links/EntityLinks";
import { ContentLoading } from "../../../components/Loading";
import { EmptyState } from "../../../components/EmptyState";
import { FetchErrorAlert } from "../../../components/FetchErrorAlert";
import { StatusAccordion } from "../../../components/StatusAccordion";
import { materialSearchUrl } from "../../../util/Materialperser";
import { DateGroupedAccordions, DateGroupSummary } from "../../../components/DateGroupedAccordions";
import { GetCookieValue, SetCookie } from "../../../util/CookieManager";
import { carryInRow } from "../../../schema/console/carry_in";
import { WorkBase } from "../../../schema/generated/Work";
import {
  CarryInSelectingWorkCacheKey, MaterialsById, carriedInDateKey, carriedInEpoch, distinctLotCount, isCarryInComplete,
  isCarryInOver, mainMaterialLabel, resolvedMaterialCount, workLabel,
} from "./helpers";
import { CarryInLotTable } from "./CarryInLotTable";

export function CarryInListPage({ pageContext, workList, materialsById }: { pageContext: PageContext; workList: WorkBase[]; materialsById: MaterialsById }) {
  const api = pageContext.api;
  const navigate = useNavigate();
  const [workId, setWorkId] = useState<number | "">("");
  const [rows, setRows] = useState<carryInRow[] | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [tab, setTab] = useState(0);

  // 工事リスト確定後、cookie が有効ならそれを、無ければ先頭工事を選ぶ。
  useEffect(() => {
    if (workId === "" && workList.length > 0) {
      const cached = GetCookieValue(CarryInSelectingWorkCacheKey);
      const cachedId = cached != null && cached !== "" ? Number(cached) : NaN;
      if (Number.isFinite(cachedId) && workList.some((w) => w.id === cachedId)) {
        setWorkId(cachedId);
      } else {
        const first = workList[0];
        if (first !== undefined) {
          setWorkId(first.id);
        }
      }
    }
  }, [workList]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (workId === "") { setRows([]); return; }
    let active = true;
    setRows(null);
    setFetchError(false);
    api.carry_in.getFromWorkId({ id: workId })
      .then((data) => { if (active) { setRows(data); } })
      .catch(() => { if (active) { setRows([]); setFetchError(true); } });
    return () => { active = false; };
  }, [api, workId]);

  const onWorkChange = (next: number | "") => {
    setWorkId(next);
    SetCookie(CarryInSelectingWorkCacheKey, next === "" ? "" : String(next), { maxAge: 365 * Time.ADay });
  };

  // 搬入日(完了)降順。未完了/未搬入は先頭、同条件は登録日降順。完了判定は一覧チップと統一。
  const sorted = useMemo(() => {
    return [...(rows ?? [])].sort((a, b) => {
      const ka = isCarryInComplete(a) ? carriedInEpoch(a) : 0;
      const kb = isCarryInComplete(b) ? carriedInEpoch(b) : 0;
      if (ka === 0 && kb === 0) { return b.createdAt - a.createdAt; }
      if (ka === 0) { return -1; }
      if (kb === 0) { return 1; }
      return kb - ka;
    });
  }, [rows]);

  return (
    <Stack p={3} gap={2} maxWidth={1400} mx="auto">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" fontWeight="bold">搬入一覧</Typography>
        <Stack direction="row" gap={2} alignItems="center">
          <WorkSelectField options={workList.map((w) => ({ id: w.id, label: workLabel(w) }))} value={workId}
            onChange={onWorkChange} placeholder={workList.length === 0 ? "工事未登録" : "工事を選択"} />
          <Button variant="contained" onClick={() => navigate("new/")} disabled={workId === ""}>新規搬入</Button>
        </Stack>
      </Stack>
      <Alert severity="info" sx={{ py: 0.5 }}>気温・湿度は任意項目です。記録しておくと品質管理報告書の温湿度推移表に反映されます。</Alert>
      <Paper variant="outlined">
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tab label="搬入一覧" />
          <Tab label="搬入日" />
        </Tabs>
        <Box>
          {rows === null && <ContentLoading />}
          {rows !== null && fetchError && <FetchErrorAlert />}
          {rows !== null && !fetchError && tab === 0 && (
            <Stack p={1} gap={1}>
              {sorted.map((ci) => <CarryInAccordion key={ci.id} ci={ci} materialsById={materialsById} />)}
              {sorted.length === 0 && <EmptyState message="搬入履歴なし" />}
            </Stack>
          )}
          {rows !== null && !fetchError && tab === 1 && <CarriedInDateGroups rows={sorted} materialsById={materialsById} />}
        </Box>
      </Paper>
    </Stack>
  );
}

function CarryInAccordion({ ci, materialsById }: { ci: carryInRow; materialsById: MaterialsById }) {
  const navigate = useNavigate();
  const carried = isCarryInComplete(ci);
  const received = resolvedMaterialCount(ci);
  const lots = distinctLotCount(ci);
  const serials = ci.Materials.filter((m) => m.Lot != null).map((m) => m.id);
  const matLabel = mainMaterialLabel(ci, materialsById);
  const header = (
    <>
      <DataLink type="carry-in" id={ci.id} onClick={(e) => e.stopPropagation()} />
      {carried && <Chip size="small" color="success" variant="outlined" label={`搬入日 ${new Time(carriedInEpoch(ci)).Format("YYYY/MM/dd")}`} />}
      {!carried && <Chip size="small" color="warning" variant="outlined" label={`未搬入  ${received}/${ci.require_material_count}`} />}
      {isCarryInOver(ci) && <Chip size="small" color="error" variant="outlined" label={`搬入個数超過 ${received}/${ci.require_material_count}`} />}
      {(ci.temperature != null || ci.humidity != null) && (
        <Chip size="small" variant="outlined" label={`${ci.temperature != null ? `${ci.temperature}℃` : "-"} / ${ci.humidity != null ? `${ci.humidity}%` : "-"}`} />
      )}
      {matLabel !== "" && <Chip size="small" variant="outlined" label={matLabel} />}
      <Chip size="small" variant="outlined" label={`${lots}ロット / ${ci.require_material_count}缶`} />
      <Box flex={1} />
      <Typography variant="caption" color="text.secondary">{ci.User.name}</Typography>
    </>
  );
  return (
    <StatusAccordion header={header}>
      <Stack gap={1}>
        {!carried && (
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Button variant="outlined" size="small" onClick={(e) => { e.stopPropagation(); navigate(`/console/carry-in/${ci.id}/scan/`); }}>搬入QR読み込み</Button>
            <Typography variant="caption" color="text.secondary">QRを読み込んで搬入を確定します</Typography>
          </Stack>
        )}
        <CarryInLotTable ci={ci} materialsById={materialsById} />
        {serials.length > 0 && (
          <Box>
            <Button size="small" variant="text" onClick={() => navigate(materialSearchUrl(serials))}>搬入材料を検索</Button>
          </Box>
        )}
      </Stack>
    </StatusAccordion>
  );
}

function CarriedInDateGroups({ rows, materialsById }: { rows: carryInRow[]; materialsById: MaterialsById }) {
  const summaryOf = (list: carryInRow[]): DateGroupSummary => ({
    completedCount: list.filter(isCarryInComplete).length,
    totalCount: list.length,
    plannedTotal: list.reduce((s, ci) => s + ci.require_material_count, 0),
    attachedTotal: list.reduce((s, ci) => s + resolvedMaterialCount(ci), 0),
  });

  return (
    <DateGroupedAccordions
      rows={rows}
      groupKeyOf={carriedInDateKey}
      summaryOf={summaryOf}
      dateChipLabel="搬入日"
      countUnitLabel="搬入"
      attachedUnitLabel="搬入"
      renderRow={(ci) => <CarryInAccordion key={ci.id} ci={ci} materialsById={materialsById} />}
      emptyState={<EmptyState message="搬入履歴なし" />}
    />
  );
}
